/* eslint-disable no-alert */
import { Injectable } from '@angular/core';
import { combineLatest, from, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';
import { LocalStoreService } from 'src/lib/services/stores/local-store/local-store.service';
import { TypedStoreType } from 'src/lib/types/typed-storage';
import { WebNotificationPurpose } from './web-notification-purpose.enum';

const GABBY_NOTIFICATIONS_ENABLED_KEY = 'gabby.main.notification.enabled';

@Injectable({
	providedIn: 'root',
})
export class WebNotificationService {
	public static featureEnabled = 'Notification' in window;
	private hasRequested: boolean = false;

	private readonly permissionRequested = new Subject<void>();

	constructor(private readonly localStore: LocalStoreService) {}

	public isEnabled = (purpose: WebNotificationPurpose): boolean => {
		switch (purpose) {
			case WebNotificationPurpose.Gabby:
				return this.localStore.get(
					GABBY_NOTIFICATIONS_ENABLED_KEY,
					TypedStoreType.BOOLEAN,
					true,
				);
			case WebNotificationPurpose.WonderSchedule:
				return true;
			default:
				throw new Error(`Unknown notification purpose: ${purpose}`);
		}
	};

	public setEnabled = (
		purpose: WebNotificationPurpose,
		enabled: boolean,
	): void => {
		switch (purpose) {
			case WebNotificationPurpose.Gabby:
				this.localStore.set(GABBY_NOTIFICATIONS_ENABLED_KEY, enabled);
				return;
			case WebNotificationPurpose.WonderSchedule:
				throw new Error(
					`Notification purpose does not have an enabled setting: ${purpose}`,
				);
			default:
				throw new Error(`Unknown notification purpose: ${purpose}`);
		}
	};

	public enabled$ = (purpose: WebNotificationPurpose): Observable<boolean> => {
		switch (purpose) {
			case WebNotificationPurpose.Gabby:
				return this.localStore.observe$(
					GABBY_NOTIFICATIONS_ENABLED_KEY,
					TypedStoreType.BOOLEAN,
					true,
				);
			case WebNotificationPurpose.WonderSchedule:
				return of(true);
			default:
				throw new Error(`Unknown notification purpose: ${purpose}`);
		}
	};

	public create = (
		purpose: WebNotificationPurpose,
		title: string,
		options?: NotificationOptions,
	): Notification => {
		if (!this.canCreate(purpose)) return null;
		else return new Notification(title, options);
	};

	public canCreate = (purpose: WebNotificationPurpose): boolean => {
		return (
			this.isEnabled(purpose) &&
			WebNotificationService.featureEnabled &&
			Notification.permission === 'granted'
		);
	};

	public canCreate$ = (
		purpose: WebNotificationPurpose,
	): Observable<boolean> => {
		return combineLatest([
			this.enabled$(purpose),
			this.permissionRequested.pipe(startWith(null)),
		]).pipe(
			map(() => this.canCreate(purpose)),
			distinctUntilChanged(),
		);
	};

	public requestPermission = (
		purpose: WebNotificationPurpose,
		aggressive: boolean = false,
	): Observable<boolean> => {
		return new Observable((s) => {
			const complete = (val: boolean) => {
				s.next(val);
				s.complete();
			};

			if (!aggressive && !this.isEnabled(purpose)) complete(false);
			else if (!WebNotificationService.featureEnabled) complete(false);
			else if (Notification.permission === 'granted') complete(true);
			else if (Notification.permission === 'denied') {
				if (aggressive) {
					alert(
						`We cannot request permission for notifications because you have blocked them. Unblock them from your browser settings to enable notifications. For instructions go here: https://bit.ly/2XT4hHY`,
					);
					complete(false);
				} else {
					complete(false);
				}
			} else {
				if (!this.hasRequested || aggressive) {
					this.hasRequested = true;
					from(Notification.requestPermission())
						.pipe(
							tap(() => this.permissionRequested.next()),
							map((x) => x === 'granted'),
						)
						.subscribe(complete);
				} else {
					complete(false);
				}
			}
		});
	};
}
